import { withEquipmentType } from '@/app/considerations/appliesToPublishCheck';
import { useAppliesToGuard } from '@/db/useAppliesToGuard';
import { useBusyAction } from '@/db/useBusyAction';
import logger from '@/util/logger';
import { EquipmentTypeDao } from '@rock/db-admin';
import type { EquipmentTypeId } from '@rock/types';
import { toast } from 'sonner';

const log = logger('useDiscardEquipmentType');
const equipmentTypeDao = new EquipmentTypeDao();

/**
 * Discard a type that was never published (its draft is all there is). A published type is retired
 * instead, so the equipment, models and recalls that name it keep resolving.
 */
export function useDiscardEquipmentType() {
    const { busy, run } = useBusyAction(log);
    const guard = useAppliesToGuard();

    return {
        busy,
        discardEquipmentType: (typeId: EquipmentTypeId) =>
            run('Discard', async () => {
                if (await equipmentTypeDao.getEquipmentType(typeId)) {
                    throw new Error(`${typeId} is published. Retire it instead.`);
                }
                await guard(`Discarding ${typeId}`, (catalog) => withEquipmentType(catalog, typeId, undefined));
                await equipmentTypeDao.discardEquipmentTypeDraft(typeId);
                toast.success('Equipment Type Discarded');
                log.info('Discarded equipment type draft', { typeId });
            }),
    };
}
